import { ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider
} from 'react-router-dom';
import { LoginScreen } from '@/screens/auth/LoginScreen';
import { AcceptInviteScreen } from '@/screens/auth/AcceptInviteScreen';
import { MainLayout } from '@/layouts/MainLayout';
import { DashboardScreen } from '@/screens/dashboard/DashboardScreen';
import { MeetingsScreen } from '@/screens/meetings/MeetingsScreen';
import { MeetingDetailScreen } from '@/screens/meetings/MeetingDetailScreen';
import { SystemMapScreen } from '@/screens/system-map/SystemMapScreen';
import { RevenueModelScreen } from '@/screens/revenue-model/RevenueModelScreen';
import { useAuthStore } from '@/store/authStore';

function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuthStore(s => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const token = useAuthStore(s => s.token);
  if (token) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Routes:
 *   /login                 email + password
 *   /accept-invite         set password from invite token
 *   /                      everything-at-a-glance dashboard
 *   /meetings              board meeting records (list)
 *   /meetings/:meetingId   board meeting detail
 *
 * Anything else falls through to /. Meetings views were ported from
 * the publisher-dashboard shareholder portal 2026-05-29 (task #142);
 * the legacy /shareholder-portal/meetings* routes there still serve
 * as a fallback for now but will be removed in a follow-up commit
 * once this build deploys cleanly.
 */
const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RedirectIfAuthed>
        <LoginScreen />
      </RedirectIfAuthed>
    )
  },
  {
    path: '/accept-invite',
    element: <AcceptInviteScreen />
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <MainLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardScreen /> },
      { path: 'meetings', element: <MeetingsScreen /> },
      { path: 'meetings/:meetingId', element: <MeetingDetailScreen /> },
      { path: 'system-map', element: <SystemMapScreen /> },
      { path: 'revenue-model', element: <RevenueModelScreen /> }
    ]
  },
  { path: '*', element: <Navigate to="/" replace /> }
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
